import { useState } from "react";
import {
  Box,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Radio,
  ListItemText
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import SearchRounded from "@mui/icons-material/SearchRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import ArrowDropDownRounded from "@mui/icons-material/ArrowDropDownRounded";

import type { Activity, ActivityType } from "@/data/mock-activities";

// ---- Public types + helpers ----------------------------------------------

/**
 * Type filter is the one axis users actually reach for — Alpaca and
 * Robinhood both lead with it. `all` keeps the stream as-is. We
 * deliberately omit Robinhood's long-tail types (rewards, corporate
 * actions, Gold billing, etc.) — not in our mock dataset.
 */
export type ActivityTypeFilter = "all" | ActivityType;

export interface ActivitiesFilterState {
  /** Free-text search across description + ticker. */
  search: string;
  /** Activity type filter. `all` = no filter. */
  type: ActivityTypeFilter;
}

export const DEFAULT_ACTIVITIES_FILTER: ActivitiesFilterState = {
  search: "",
  type: "all"
};

const TYPE_OPTIONS: ReadonlyArray<{ value: ActivityTypeFilter; label: string }> = [
  { value: "all",      label: "All types" },
  { value: "trade",    label: "Trades" },
  { value: "dividend", label: "Dividends" },
  { value: "fee",      label: "Fees" },
  { value: "transfer", label: "Transfers" },
  { value: "interest", label: "Interest" }
];

function typeLabel(v: ActivityTypeFilter): string {
  return TYPE_OPTIONS.find((o) => o.value === v)?.label ?? "All types";
}

/**
 * Apply a filter state to a list of activities. Matches by description
 * + ticker (case-insensitive) for the search term, and by exact type
 * otherwise. `all` short-circuits the type check.
 */
export function applyActivitiesFilter(
  activities: Activity[],
  f: ActivitiesFilterState
): Activity[] {
  const q = f.search.trim().toLowerCase();
  // Normalize the filter type — defensive against weird casing or
  // whitespace creeping in from any caller. The whitelist also acts
  // as a hard guard: an unrecognized value falls through to "all"
  // (show everything) instead of silently emptying the list.
  const validTypes: ReadonlySet<string> = new Set([
    "trade",
    "dividend",
    "fee",
    "transfer",
    "interest"
  ]);
  const filterType =
    typeof f.type === "string"
      ? validTypes.has(f.type.trim().toLowerCase())
        ? f.type.trim().toLowerCase()
        : "all"
      : "all";
  return activities.filter((a) => {
    if (filterType !== "all") {
      const aType =
        typeof a.type === "string" ? a.type.trim().toLowerCase() : "";
      if (aType !== filterType) return false;
    }
    if (!q) return true;
    const hay = [a.description, a.ticker ?? "", a.assetName ?? ""]
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });
}

// ---- Component -----------------------------------------------------------

interface Props {
  filter: ActivitiesFilterState;
  onChange: (next: ActivitiesFilterState) => void;
  sx?: SxProps<Theme>;
}

/**
 * Filter bar for the Activities tab. Mirrors the Robinhood pattern
 * from the reference screenshots: a type chip on the left (with a
 * dropdown menu when tapped) and a search field on the right. The
 * Alpaca version has a date-range picker; we've left that out for now
 * since our seeded dataset spans a known ~5-week window and the
 * date-picker would be UI weight without a real payoff.
 */
export function ActivitiesFilterBar({ filter, onChange, sx }: Props) {
  const [typeAnchor, setTypeAnchor] = useState<HTMLElement | null>(null);

  const updateSearch = (search: string) => onChange({ ...filter, search });
  const updateType = (type: ActivityTypeFilter) => {
    setTypeAnchor(null);
    onChange({ ...filter, type });
  };

  const typeActive = filter.type !== "all";

  return (
    <Box sx={sx}>
      <Stack direction="row" spacing={1} alignItems="center">
        {/* Type chip — tapping opens a radio-style menu that matches
         *  Robinhood's "Transaction type" sheet layout. Keeping the chip
         *  styling identical across Active/Inactive avoids a jarring
         *  color shift when the user picks a type. */}
        <Chip
          clickable
          size="small"
          label={typeLabel(filter.type)}
          onClick={(e) => setTypeAnchor(e.currentTarget)}
          onDelete={typeActive ? () => updateType("all") : undefined}
          deleteIcon={
            typeActive ? (
              <CloseRounded sx={{ fontSize: 16 }} />
            ) : (
              <ArrowDropDownRounded sx={{ fontSize: 20 }} />
            )
          }
          sx={{
            fontWeight: 600,
            // Apr-2026 typography sweep: body1 (1rem) + proportional
            // height bump so the type chip reads at the same scale as
            // the "Buying Power" row and the range chips above.
            fontSize: "1rem",
            px: 0.25,
            height: 36,
            borderRadius: 18,
            "& .MuiChip-label": { px: 1.25 },
            "& .MuiChip-deleteIcon": {
              color: "text.secondary",
              "&:hover": { color: "text.primary" }
            }
          }}
        />

        {/* Search field fills the rest of the row. On Robinhood this is
         *  behind a magnifier icon tap; we expose it inline since users
         *  on mobile already expect search to be visible here (pattern
         *  set by Orders/Bots/Positions filter bars). */}
        <TextField
          size="small"
          value={filter.search}
          onChange={(e) => updateSearch(e.target.value)}
          placeholder="Search description or ticker"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 0.5 }}>
                <SearchRounded sx={{ fontSize: 18, color: "text.secondary" }} />
              </InputAdornment>
            ),
            endAdornment: filter.search ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => updateSearch("")}
                  aria-label="Clear search"
                >
                  <CloseRounded sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 18,
              // Apr-2026 typography sweep: body1 (1rem) + height to
              // match the chip next to it.
              fontSize: "1rem",
              height: 36,
              bgcolor: "background.paper"
            },
            "& .MuiOutlinedInput-input": { py: 0.25 }
          }}
        />
      </Stack>

      {/* Type-picker menu — matches the Robinhood radio sheet. Width
       *  capped so option labels don't stretch the anchor; long list
       *  scrolls if we add more types later. */}
      <Menu
        open={Boolean(typeAnchor)}
        anchorEl={typeAnchor}
        onClose={() => setTypeAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { minWidth: 200 } } }}
      >
        {TYPE_OPTIONS.map((o) => (
          <MenuItem
            key={o.value}
            selected={filter.type === o.value}
            onClick={() => updateType(o.value)}
            sx={{ py: 0.75 }}
          >
            <Radio
              checked={filter.type === o.value}
              size="small"
              sx={{ p: 0.5, mr: 1 }}
            />
            <ListItemText primary={o.label} />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
